import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LoadingService } from '../../core/services/loading.service';
import { FileService } from '../../core/api/file.service';
import { HelpersService } from '../../core/services/helpers.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-file-preview',
  standalone: true,
  imports: [],
  templateUrl: './file-preview.component.html',
  styleUrl: './file-preview.component.scss',
})
export class FilePreviewComponent implements OnInit {
  fileUrl: SafeResourceUrl | null = null;
  isPdf = false;
  isImage = false;

  constructor(
    private dialog: MatDialogRef<FilePreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loadingService: LoadingService,
    private fileService: FileService,
    private helpers: HelpersService,
    private sanitizer: DomSanitizer
  ) {}

  closeDialog(): void {
    this.dialog.close();
  }

  private getFileMeta(): { id: any; fileName: string; fileType: string; path: string } | null {
    const source = this.data?.file ?? this.data;
    const id = source?.id ?? this.data?.fileId;
    if (!id) return null;

    return {
      id,
      fileName: source?.fileName ?? this.data?.fileName ?? 'downloaded-file',
      fileType: source?.fileType ?? this.data?.fileType ?? '',
      path: source?.path ?? this.data?.path ?? '',
    };
  }

  private normalizeMime(type?: string): string {
    return (type || '').toLowerCase().split(';')[0].trim();
  }

  private resolvePreviewType(
    fileMeta: { fileName: string; fileType: string; path: string },
    blobType?: string
  ): string {
    const declaredType = this.normalizeMime(fileMeta.fileType);
    const responseType = this.normalizeMime(blobType);
    const fileName = (fileMeta.fileName || '').toLowerCase();
    const filePath = (fileMeta.path || '').toLowerCase();

    if (declaredType && declaredType !== 'application/octet-stream') return declaredType;
    if (responseType && responseType !== 'application/octet-stream') return responseType;

    if (fileName.endsWith('.pdf') || filePath.endsWith('.pdf')) return 'application/pdf';
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(fileName) || /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(filePath)) {
      return 'image/*';
    }

    return declaredType || responseType;
  }

  private openBlobInIframe(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    this.isPdf = true;
    this.isImage = false;
    this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private async detectBlobKind(blob: Blob): Promise<'pdf' | 'image' | 'unknown'> {
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());

    // PDF signature: %PDF-
    if (
      bytes.length >= 5 &&
      bytes[0] === 0x25 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x44 &&
      bytes[3] === 0x46 &&
      bytes[4] === 0x2d
    ) {
      return 'pdf';
    }

    // PNG / JPG / GIF / WEBP / BMP
    const isPng =
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47;
    const isJpg =
      bytes.length >= 3 &&
      bytes[0] === 0xff &&
      bytes[1] === 0xd8 &&
      bytes[2] === 0xff;
    const isGif =
      bytes.length >= 4 &&
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38;
    const isWebp =
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50;
    const isBmp = bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d;

    if (isPng || isJpg || isGif || isWebp || isBmp) return 'image';
    return 'unknown';
  }

  download(): void {
    const fileMeta = this.getFileMeta();
    if (!fileMeta) {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
      return;
    }

    this.loadingService.startLoading();
    this.fileService.download(fileMeta.id).subscribe(
      (res) => {
        this.loadingService.endLoading();
        const url = URL.createObjectURL(res);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.download = fileMeta.fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  ngOnInit(): void {
    const fileMeta = this.getFileMeta();
    if (!fileMeta) {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
      this.closeDialog();
      return;
    }

    this.loadingService.startLoading();
    this.fileService.download(fileMeta.id).subscribe(
      async (res) => {
        this.loadingService.endLoading();

        const type = this.resolvePreviewType(fileMeta, res?.type);
        const detectedKind = await this.detectBlobKind(res);

        if (detectedKind === 'pdf' || type.includes('pdf')) {
          const blob = new Blob([res], { type: 'application/pdf' });
          this.openBlobInIframe(blob);
        } else if (detectedKind === 'image' || type?.startsWith('image/')) {
          this.isImage = true;
          const url = URL.createObjectURL(res);
          this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else {
          this.helpers.sbError(
            'File tidak bisa dipreview. Silakan gunakan tombol Download.'
          );
        }
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }
}
